import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { queryListings } from '../../ducks/SdkDataAccessLayer.duck';
import {
    getDynamicLpsCanton,
    getDynamicLpsCantons,
    getDynamicLpsCity,
    getDynamicLpsLocations,
    getDynamicLpsNearby,
} from '../../util/api';
import { calculateBoundsFromLPData, getLocationFromCoordinates } from './Pages/helpers';
const { listingTypeHorse } = config;

const SET_DYNAMIC_DATA_ERROR = 'SeoLandingPages/SET_DYNAMIC_DATA_ERROR';
const SET_META_BY_COUNTRY = 'SeoLandingPages/SET_META_BY_COUNTRY';
const SET_DYNAMIC_FIELD_BY_NAME = 'SeoLandingPages/SET_DYNAMIC_FIELD_BY_NAME';

// ================ Reducer ================ //

const initialState = {
    metadata: {},
    dynamicDataFetchError: null,
    landingData: {},
    listingsParticipationData: {},
    listOfLocations: null,
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload, dynamicFieldName } = action;

    switch (type) {
        case SET_META_BY_COUNTRY:
            return {
                ...state,
                metadata: { ...state.metadata, [payload.code]: payload.data },
            };

        case SET_DYNAMIC_DATA_ERROR:
            return { ...state, dynamicDataFetchError: payload };
        case SET_DYNAMIC_FIELD_BY_NAME:
            return { ...state, [dynamicFieldName]: payload };

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const setMetaByCountry = payload => ({ type: SET_META_BY_COUNTRY, payload });
export const setDynamicDataError = payload => ({ type: SET_DYNAMIC_DATA_ERROR, payload });
export const setDynamicFieldByName = (payload, dynamicFieldName) => ({
    type: SET_DYNAMIC_FIELD_BY_NAME,
    payload,
    dynamicFieldName,
});

// ================ Thunks ================ //

export const fetchListingsByCountryCode = params => async (dispatch, getState, sdk) => {
    try {
        const listings = await sdk.listings.query(params);
        dispatch(
            setMetaByCountry({
                code: params.pub_userCountry,
                data: listings.data.meta.totalItems,
            })
        );
        return listings;
    } catch (e) {}
};

const dejsonify = async handler => {
    const response = await handler();
    const data = await response.json();
    return data;
};

const getCantonOrCityData = ({ canton, city, country }) => async dispatch => {
    const handler = city ? getDynamicLpsCity : getDynamicLpsCanton;

    const data = await dejsonify(() => handler({ country, canton, city }));

    dispatch(setDynamicFieldByName(data, 'landingData'));
};

const getNearbyData = ({ city, country }) => async dispatch => {
    const data = await dejsonify(() => getDynamicLpsNearby({ country, city }));

    dispatch(setDynamicFieldByName(data, 'listingsParticipationData'));
};

const getCantonsData = ({ canton, country }) => async dispatch => {
    const data = await dejsonify(() => getDynamicLpsCantons({ country, canton }));
    dispatch(setDynamicFieldByName(data, 'listingsParticipationData'));

    if (canton) {
        const data = await dejsonify(() => getDynamicLpsLocations({ canton }));
        dispatch(setDynamicFieldByName(data, 'listOfLocations'));
    }
};

export const fetchDynamicLpsData = (urlParams, queryParams) => async (dispatch, getState, sdk) => {
    const calls = [];
    const { canton, city } = urlParams || {};

    try {
        if (canton || city) {
            calls.push(await dispatch(getCantonOrCityData(urlParams)));
        }
        if (city) {
            calls.push(await dispatch(getNearbyData(urlParams)));
        } else {
            calls.push(await dispatch(getCantonsData(urlParams)));
        }

        const response = await Promise.all(calls);

        const { landingData } = getState().SeoLandingPages;

        const bounds = calculateBoundsFromLPData({ landingData, ...urlParams });
        const origin = getLocationFromCoordinates(landingData);
        const countryIso = urlParams.country.toLowerCase() ? 'CH' : 'DE';

        const commonParams = {
            include: ['author', 'images'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
            pub_activities: `has_any:${queryParams.pub_activities}`,
            per_page: 9,
            pub_type: listingTypeHorse,
            pub_userCountry: countryIso,
            origin,
            ...(queryParams || {}),
        };

        const preFetchedListingsResp = await dispatch(
            queryListings(bounds ? { ...commonParams, bounds } : commonParams)
        );
        const backupListingsResp = await dispatch(queryListings(commonParams));

        const preFetchedListings = preFetchedListingsResp.data.data;
        const backupListings = backupListingsResp.data.data;
        const uniqueBackupListings =
            backupListings?.filter(
                bl => !preFetchedListings.some(pfl => pfl.id.uuid === bl.id.uuid)
            ) || [];

        dispatch(
            setDynamicFieldByName(preFetchedListings.concat(uniqueBackupListings), 'listings')
        );
        dispatch(
            setDynamicFieldByName(
                (city && !preFetchedListings) || (city && preFetchedListings.length < 1),
                'noListingsForLocation'
            )
        );
        dispatch(setDynamicFieldByName(origin, 'location'));

        return response;
    } catch (e) {
        dispatch(setDynamicDataError(e.message || 'Something went wrong, try later.'));
    }
};

export const fetchActivitiesListings = (urlParams, queryParams) => async dispatch => {
    const activityDays = ['1-2', '2-3', '3-5'];
    const countryIso = urlParams.country.toLowerCase() === 'schweiz' ? 'CH' : 'DE';

    try {
        const responses = await Promise.all(
            activityDays.map(
                async dayRange =>
                    await dispatch(
                        queryListings({
                            include: ['author', 'images'],
                            'fields.image': [
                                'variants.landscape-crop',
                                'variants.landscape-crop2x',
                            ],
                            per_page: 3,
                            pub_type: listingTypeHorse,
                            pub_userCountry: countryIso,
                            pub_availability: dayRange,
                            ...(queryParams || {}),
                        })
                    )
            )
        );

        responses.forEach(response => dispatch(addMarketplaceEntities(response)));
    } catch (e) {
        // to do
    }
};

export const loadData = () => async (dispatch, getState, sdk) =>
    Promise.all([
        dispatch(
            fetchListingsByCountryCode({
                per_page: 1,
                pub_type: listingTypeHorse,
                pub_userCountry: 'DE',
            })
        ),
        dispatch(
            fetchListingsByCountryCode({
                per_page: 1,
                pub_type: listingTypeHorse,
                pub_userCountry: 'CH',
            })
        ),
    ]);

export const loadDynamicData = (urlParams, queryParams) => dispatch =>
    Promise.all([
        dispatch(loadData()),
        dispatch(
            fetchDynamicLpsData(
                { ...urlParams, country: urlParams.country.toLowerCase() },
                queryParams
            )
        ),
        dispatch(
            fetchActivitiesListings(
                { ...urlParams, country: urlParams.country.toLowerCase() },
                queryParams
            )
        ),
    ]);

export const loadWalkingParticipationData = params => dispatch =>
    dispatch(
        loadDynamicData(params, {
            pub_activities: 'Spazieren',
        })
    );

export const loadFindRidingShareData = params => dispatch =>
    dispatch(
        loadDynamicData(params, {
            pub_activities: 'has_any:Ausreiten,Training,Turniere',
        })
    );

export const loadCareParticipationData = params => dispatch =>
    dispatch(
        loadDynamicData(params, {
            pub_activities: 'Pflegen',
        })
    );
